import { type ConnectionLineComponentProps, type InternalNode, type Node } from '@xyflow/react'

import { GraphColors } from '../../../bpmn-graph.constants'
import { initialSequencePoints, type Rect } from '../bpmn-graph.mapper'
import { useConnectionTargetResolver } from '../connection-target.context'
import SequenceArrowMarker from './SequenceArrowMarker'
import { buildWaypointsPath, computeStepPolyline } from './orthogonal-edge.util'

const MARKER_ID = 'bpmn-connection-arrow'

function internalRect(node: InternalNode<Node>): Rect | null {
  const width = node.measured?.width
  const height = node.measured?.height

  return width && height
    ? {
        x: node.internals.positionAbsolute.x,
        y: node.internals.positionAbsolute.y,
        width,
        height,
      }
    : null
}

/** Live connection gesture rendered by the same explicit-point router as edges. */
export default function SequenceConnectionLine({
  fromNode,
  fromX,
  fromY,
  fromPosition,
  toX,
  toY,
  toPosition,
}: ConnectionLineComponentProps) {
  const resolveConnectionTarget = useConnectionTargetResolver()
  const sourceRect = internalRect(fromNode)
  // When React Flow has snapped to a handle this is that handle's point, which
  // sits on its node's border and so resolves to the same node and port; every
  // other frame it is the raw pointer.
  const pointer = { x: toX, y: toY }
  const resolved = resolveConnectionTarget(pointer, fromNode.id)
  // A gesture starts inside its own source node. Landing an arrow back on it
  // would be noise, so the line keeps following the pointer until it leaves.
  const target = resolved?.reason === 'self-connection' ? null : resolved
  // The endpoint the drop will persist, not the pointer: initialSequencePoints
  // is what withInitialSequencePoints stores on creation.
  const points =
    target && sourceRect
      ? initialSequencePoints(sourceRect, target.rect, target.position)
      : computeStepPolyline({ x: fromX, y: fromY }, fromPosition, pointer, toPosition)
  const color = target && !target.valid ? GraphColors.red_500 : GraphColors.neutral_300

  return (
    <>
      {target && (
        <SequenceArrowMarker
          id={MARKER_ID}
          color={color}
        />
      )}
      <path
        d={buildWaypointsPath(points)}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        markerEnd={target ? `url(#${MARKER_ID})` : undefined}
      />
    </>
  )
}
